import { ca } from '@cian/analytics';
import { EMyHomeEstimationTargets } from '../types/estimationTarget';

interface IMyHomeAnalyticsProps {
  estimationTarget: EMyHomeEstimationTargets;
  value?: number | string;
  auto: boolean;
  isNewbuilding?: boolean;
}

export interface ITrackAddNewFlatByNumberParameters {
  autofiiled?: {
    roomsnumber?: number;
    area?: number;
  };
  result: {
    address: string;
    flatnumber?: number;
    roomsnumber?: number;
    area?: number;
    landarea?: number;
    chatEnabled?: boolean;
  };
}

export interface ITrackAddNewMyHomeObjectProps {
  estimationTarget: EMyHomeEstimationTargets;
  label: string;
  parameters: ITrackAddNewFlatByNumberParameters;
  isNewbuilding?: boolean;
}

interface IGetCaEventCategoriesProps {
  estimationTarget: EMyHomeEstimationTargets;
  isNewbuilding?: boolean;
}

const caEventCategories: { [K in EMyHomeEstimationTargets]: string } = {
  [EMyHomeEstimationTargets.flat]: 'MyHomeFlat',
  [EMyHomeEstimationTargets.house]: 'MyHomeHouse',
  [EMyHomeEstimationTargets.land]: 'MyHomeLand',
};

const caEstimationTargetLabels: { [K in EMyHomeEstimationTargets]: string } = {
  [EMyHomeEstimationTargets.flat]: 'flat',
  [EMyHomeEstimationTargets.house]: 'house',
  [EMyHomeEstimationTargets.land]: 'land',
};

function getCaEventCategories(props: IGetCaEventCategoriesProps) {
  const { estimationTarget, isNewbuilding } = props;

  if (isNewbuilding) {
    return 'MyHomeNewBuilding';
  }

  return caEventCategories[estimationTarget];
}

export function trackMyHomeEstimationTargetSelect(estimationTarget: EMyHomeEstimationTargets) {
  ca('eventSite', {
    name: 'oldevent',
    category: getCaEventCategories({ estimationTarget }),
    action: 'SelectEstimateType_sopr',
    label: caEstimationTargetLabels[estimationTarget],
  });
}

export function prepareTrackMyHomeAddressPick() {
  return (rawAddress: string, suggestedAddress: string, estimationTarget: EMyHomeEstimationTargets) => {
    ca('eventSite', {
      name: 'oldevent',
      category: getCaEventCategories({ estimationTarget }),
      action: 'TypeIn_sopr',
      label: 'FullAddress',
      page: {
        extra: {
          rawAddress,
          suggestedAddress,
        },
      },
    });
  };
}

export function trackMyHomeAddressInputClick(estimationTarget: EMyHomeEstimationTargets) {
  ca('eventSite', {
    name: 'oldevent',
    category: getCaEventCategories({ estimationTarget }),
    action: 'Click_sopr',
    label: 'Address',
  });
}

export function trackMyHomeAddressInputLaterSomeSeconds(label: string, estimationTarget: EMyHomeEstimationTargets) {
  ca('eventSite', {
    name: 'oldevent',
    category: getCaEventCategories({ estimationTarget }),
    action: 'InputTimeout',
    label,
  });
}

export function trackMyHomeFlatNumberPick({ estimationTarget, value, auto, isNewbuilding }: IMyHomeAnalyticsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: getCaEventCategories({ estimationTarget, isNewbuilding }),
    action: 'TypeIn_sopr',
    label: 'FlatNumber',
    page: {
      extra: {
        value,
        isAvailable: auto,
      },
    },
  });
}

export function trackMyHomeRoomsCountClick({ estimationTarget, value, auto, isNewbuilding }: IMyHomeAnalyticsProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: getCaEventCategories({ estimationTarget, isNewbuilding }),
    action: 'Click_sopr',
    label: 'RoomsNumber',
    page: {
      extra: {
        value,
        auto,
      },
    },
  });
}

export function getTrackMyHomeAreaPick({ estimationTarget, value, auto, isNewbuilding }: IMyHomeAnalyticsProps) {
  return () => {
    ca('eventSite', {
      name: 'oldevent',
      category: getCaEventCategories({ estimationTarget, isNewbuilding }),
      action: 'TypeIn_sopr',
      label: 'Area',
      page: {
        extra: {
          value,
          auto,
        },
      },
    });
  };
}

export function getTrackMyHomeLandAreaPick(estimationTarget: EMyHomeEstimationTargets) {
  return () => {
    ca('eventSite', {
      name: 'oldevent',
      category: getCaEventCategories({ estimationTarget }),
      action: 'TypeIn_sopr',
      label: 'LandArea',
    });
  };
}

export function trackAddNewMyHomeObject({
  label,
  estimationTarget,
  parameters,
  isNewbuilding,
}: ITrackAddNewMyHomeObjectProps) {
  ca('eventSite', {
    name: 'oldevent',
    category: getCaEventCategories({ estimationTarget, isNewbuilding }),
    action: 'Click',
    label,
    page: {
      extra: {
        parameters,
      },
    },
  });
}

export function trackNewbuildingSectionSelect(sectionName: string) {
  ca('eventSite', {
    name: 'oldevent',
    category: 'MyHomeNewBuilding',
    action: 'Click_sopr',
    label: 'BuildingName',
    page: {
      extra: {
        value: sectionName,
      },
    },
  });
}
